import React, { useEffect, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Phone, ArrowRight, CheckCircle2, Sparkles, Home as HomeIcon, Award } from 'lucide-react';
import Header, { SocialRail } from '../components/Header';
import Footer from '../components/Footer';
import JsonLd from '../components/JsonLd';
import NearbyCitiesSection from '../components/NearbyCitiesSection';
import ProjectStylesSection from '../components/ProjectStylesSection';
import { getCityBySlug, cities, poolImages, COUNTY, SITE_URL } from '../data/cities';

const CityPage = () => {
  const { slug } = useParams();
  const city = getCityBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (city) {
      document.title = `Pool Builder in ${city.displayName} | Custom Pool Construction & Design`;
      const metaEl = document.querySelector('meta[name="description"]');
      if (metaEl) {
        metaEl.setAttribute(
          'content',
          `Leading pool builder in ${city.displayName}. Custom pool construction, pool remodeling & luxury pool design in ${city.name} and throughout ${COUNTY.name}. Call 407-606-6375 for a free estimate.`
        );
      }
    }
  }, [city]);

  const nearbyCities = useMemo(
    () =>
      city
        ? city.nearby.map((s) => cities.find((c) => c.slug === s)).filter(Boolean)
        : [],
    [city]
  );

  const heroImg = useMemo(
    () =>
      city
        ? poolImages[Math.abs(city.slug.length) % poolImages.length]
        : poolImages[0],
    [city]
  );

  const absHeroImg = useMemo(
    () => (heroImg.startsWith('http') ? heroImg : `${SITE_URL}${heroImg}`),
    [heroImg]
  );

  const localBusinessLd = useMemo(() => {
    if (!city) return null;
    const url = `${SITE_URL}/pool-builder/${city.slug}/`;
    return {
      '@context': 'https://schema.org',
      '@type': 'PoolBuilder',
      '@id': `${url}#localbusiness`,
      name: `American Pools & Spas — Pool Builder in ${city.displayName}`,
      image: absHeroImg,
      url,
      telephone: '+1-407-606-6375',
      priceRange: '$$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '7320 Narcoossee Road',
        addressLocality: 'Orlando',
        addressRegion: 'FL',
        postalCode: '32822',
        addressCountry: 'US',
      },
      areaServed: [
        { '@type': 'City', name: city.name, containedInPlace: COUNTY.displayName },
        { '@type': 'AdministrativeArea', name: COUNTY.displayName },
      ],
      makesOffer: city.styles.map((s) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: s },
      })),
      sameAs: [
        'https://www.facebook.com/AmericanPoolsSpas',
        'https://www.instagram.com/americanpoolsspas/',
        'https://www.youtube.com/channel/UC29-XIIPCYvfEz-ZJvhlcag',
      ],
    };
  }, [city, absHeroImg]);

  const breadcrumbLd = useMemo(() => {
    if (!city) return null;
    const url = `${SITE_URL}/pool-builder/${city.slug}/`;
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${SITE_URL}/#cities` },
        { '@type': 'ListItem', position: 3, name: city.displayName, item: url },
      ],
    };
  }, [city]);

  if (!city) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="bg-white min-h-screen" data-testid={`city-page-${city.slug}`}>
      <JsonLd data={localBusinessLd} />
      <JsonLd data={breadcrumbLd} />
      <Header />
      <SocialRail />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-[#f3f6fb] border-b border-gray-200">
        <div className="max-w-[1280px] mx-auto px-5 py-3 text-[12px] text-[#0b1e4d]">
          <Link to="/" className="hover:text-[#1e4fa3]">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/#cities" className="hover:text-[#1e4fa3]">Service Areas</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-600" aria-current="page">{city.displayName}</span>
        </div>
      </nav>

      {/* Hero (H1) */}
      <section
        className="relative h-[420px] md:h-[500px] w-full bg-cover bg-center flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(11,30,77,0.60), rgba(11,30,77,0.60)), url(${heroImg})`,
        }}
        aria-label={`Pool builder in ${city.displayName} hero`}
      >
        <div className="max-w-[1280px] mx-auto w-full px-5 text-white">
          <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#bcd5f5] mb-3">
            {COUNTY.name}, Florida
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl">
            Pool Builder in {city.displayName}
          </h1>
          <p className="mt-5 text-base md:text-lg text-white/90 max-w-2xl">
            As a leading pool builder in {city.displayName}, we specialize in
            custom pool construction, pool remodeling, and luxury pool design.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a
              href="tel:4076066375"
              aria-label={`Call American Pools and Spas in ${city.name} at 407-606-6375`}
              data-testid="city-call-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#1e4fa3] hover:bg-[#183d80] text-white font-semibold px-6 py-3 rounded-sm transition-colors"
            >
              <Phone className="w-4 h-4" /> Call 407-606-6375
            </a>
            <a
              href="https://american-pools.com/request-a-quote/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="city-quote-btn"
              className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/40 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-sm backdrop-blur-sm transition-colors"
            >
              Request a Free Estimate <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section aria-label="Trust signals" className="bg-white border-b border-gray-200 py-5">
        <div className="max-w-[1280px] mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex flex-col items-center gap-0.5">
            <Award className="w-5 h-5 text-[#1e4fa3]" />
            <p className="text-[12px] font-bold text-[#0b1e4d]">#1 Pool Builder</p>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <CheckCircle2 className="w-5 h-5 text-[#1e4fa3]" />
            <p className="text-[12px] font-bold text-[#0b1e4d]">BBB A+ Accredited</p>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <HomeIcon className="w-5 h-5 text-[#1e4fa3]" />
            <p className="text-[12px] font-bold text-[#0b1e4d]">Licensed CPC1460475</p>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <Sparkles className="w-5 h-5 text-[#1e4fa3]" />
            <p className="text-[12px] font-bold text-[#0b1e4d]">20+ Years in Florida</p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-4xl mx-auto px-5 py-14">
        <h2 className="font-serif text-2xl md:text-3xl text-[#0b1e4d] font-bold mb-4">
          Custom Pool Construction in {city.name}
        </h2>
        <p className="text-[15px] text-gray-700 leading-relaxed">
          As a leading pool builder in{' '}
          <span className="font-semibold text-[#1e4fa3]">{city.displayName}</span>,
          we specialize in custom pool construction, pool remodeling, and luxury
          pool design. American Pools &amp; Spas brings over 20 years of Florida
          pool-building expertise to every project in {city.name}, crafting
          beautiful inground swimming pools, spas, outdoor kitchens, and screen
          enclosures that become the centerpiece of your backyard.
        </p>
      </section>

      {/* Project styles */}
      <ProjectStylesSection city={city} poolImages={poolImages} />

      {/* Why choose us */}
      <section className="max-w-[1280px] mx-auto px-5 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0b1e4d] font-bold mb-5">
              Why {city.name} Homeowners Choose Us
            </h2>
            <p className="text-gray-700 leading-relaxed text-[15px] mb-6">
              We proudly serve homeowners in {city.name} and throughout {COUNTY.name}.
              From the first design consultation to the final splash, our team
              handles every detail with craftsmanship and care.
            </p>
            <ul className="space-y-3">
              {[
                '#1 Ranked Pool Builder \u2013 13 Years Running',
                'BBB A+ Accredited Business & Torch Award Winner',
                'Fully licensed (CPC1460475) & insured Florida builder',
                'Custom 3D pool design and guided planning process',
                'Dedicated project manager from permit to completion',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1e4fa3] flex-shrink-0 mt-0.5" />
                  <span className="text-[14px] text-gray-800">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a
                href="tel:4076066375"
                className="inline-flex items-center justify-center gap-2 bg-[#1e4fa3] hover:bg-[#183d80] text-white font-semibold px-6 py-3 rounded-sm transition-colors"
              >
                <Phone className="w-4 h-4" /> Schedule Consultation
              </a>
              <a
                href="https://american-pools.com/request-a-quote/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#1e4fa3] text-[#1e4fa3] hover:bg-[#1e4fa3] hover:text-white font-semibold px-6 py-3 rounded-sm transition-colors"
              >
                Request a Quote
              </a>
            </div>
          </div>
          <div className="rounded-sm overflow-hidden shadow-lg">
            <img
              src={poolImages[(city.slug.length + 2) % poolImages.length]}
              alt={`Custom luxury pool built by American Pools & Spas in ${city.displayName}`}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <NearbyCitiesSection cities={nearbyCities} countyName={COUNTY.name} />

      {/* Closing */}
      <section className="max-w-4xl mx-auto px-5 py-14 text-center">
        <p className="font-serif text-2xl md:text-3xl text-[#0b1e4d] leading-snug">
          We proudly serve homeowners in{' '}
          <span className="text-[#1e4fa3] font-bold">{city.name}</span> and
          throughout {COUNTY.name}.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
          <a
            href="tel:4076066375"
            className="inline-flex items-center justify-center gap-2 bg-[#1e4fa3] hover:bg-[#183d80] text-white font-semibold px-6 py-3 rounded-sm transition-colors"
          >
            <Phone className="w-4 h-4" /> 407-606-6375
          </a>
          <a
            href="https://american-pools.com/request-a-quote/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 border-2 border-[#1e4fa3] text-[#1e4fa3] hover:bg-[#1e4fa3] hover:text-white font-semibold px-6 py-3 rounded-sm transition-colors"
          >
            Request a Free Estimate
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CityPage;
